import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { auth, db } from '../firebase/firebaseConfig';
import { ref, onValue, query, orderByChild, equalTo } from 'firebase/database';
import './Suspended.css';

function Suspended() {
  const { userProfile, currentUser } = useAuth();
  const navigate = useNavigate();
  const [myReports, setMyReports] = useState([]);

  useEffect(() => {
    if (!currentUser) return;

    const reportsRef = ref(db, 'reports');
    const userReportsQuery = query(reportsRef, orderByChild('uid'), equalTo(currentUser.uid));

    const unsubscribe = onValue(userReportsQuery, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        // Only show 'open' reports on the suspension page
        const list = Object.values(data)
          .filter(r => r.status === 'open')
          .sort((a, b) => b.createdAt - a.createdAt);
        setMyReports(list);
      } else {
        setMyReports([]);
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleLogout = async () => {
    await auth.signOut();
    navigate('/login');
  };

  if (!userProfile || (userProfile.status !== 'suspended' && userProfile.status !== 'banned' && userProfile.status !== 'blocked')) {
    return (
      <div className="suspended-container">
        <div className="glass-card text-center">
            <h1>Access Denied</h1>
            <p>Your account is in good standing. You shouldn't be here.</p>
            <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>Go to Dashboard</button>
        </div>
      </div>
    );
  }

  const isPermanent = userProfile.suspensionType === 'permanent' || userProfile.status === 'banned' || userProfile.status === 'blocked';

  return (
    <div className="suspended-container">
      <div className="suspended-overlay"></div>
      
      <div className="suspended-card-wrapper animate-up">
        <div className="suspended-card glass-card">
          <div className="suspended-header">
            <div className="suspended-icon-hex flex-center">
              <i className={`fa-solid ${isPermanent ? 'fa-shield-halved' : 'fa-hourglass-start animate-slow-pulse'}`}></i>
            </div>
            <h1>Account Restricted</h1>
            <p className="status-indicator">
              Official Notice from <strong>Origin Core Security</strong>
            </p>
          </div>

          <div className="suspension-badge-tray">
            {userProfile.suspensionType === 'temporary' && <span className="badge-pill warning">TEMPORARY RESTRICTION</span>}
            {userProfile.suspensionType === 'permanent' && <span className="badge-pill critical">PERMANENT DEACTIVATION</span>}
            {userProfile.suspensionType === 'AI Block' && <span className="badge-pill security">AI SECURITY BLOCK</span>}
            {!userProfile.suspensionType && <span className="badge-pill warning">SUSPENDED</span>}
          </div>

          <div className="info-structure">
            <section className="info-block">
              <label><i className="fa-solid fa-circle-exclamation"></i> Reason for Action</label>
              <h3>{userProfile.suspensionReason || "Security Policy Violation"}</h3>
            </section>

            <section className="info-block highlight">
              <label><i className="fa-solid fa-fingerprint"></i> Policy Violation Details</label>
              <div className="violation-description">
                {userProfile.suspensionDetail || "Our automated systems have detected inconsistencies between your registered details and platform identity standards. This measure is taken to preserve developer data integrity."}
              </div>
            </section>

            <section className="info-block">
              <label><i className="fa-solid fa-arrow-right-long"></i> Next Steps</label>
              <p>
                Please review our <a href="/terms">Terms of Service</a> or submit a formal appeal to our security board for manual account verification.
              </p>
            </section>
          </div>

          <div className="help-box">
            <p>
              <i className="fa-solid fa-headset"></i> Need assistance? Contact <strong>support@devtracker.com</strong> or track your open cases below.
            </p>
          </div>

          <div className="suspended-actions">
            <button className="btn btn-outline" onClick={handleLogout}>
              <i className="fa-solid fa-right-from-bracket"></i> Log Out
            </button>
            <a href="/report" className="btn btn-primary gradient-btn">
              <i className="fa-solid fa-paper-plane"></i> Submit Appeal
            </a>
          </div>
        </div>

        {/* Reports Tracking Section - Standard Dashboard Style */}
        {myReports.length > 0 && (
          <div className="suspended-reports-card glass-card animate-up-delayed">
            <div className="card-header">
              <h3>Active Appeals & Reports</h3>
              <p>Track the real-time status of your recovery requests.</p>
            </div>
            
            <div className="table-wrapper">
              <table className="modern-table">
                <thead>
                  <tr>
                    <th>CASE ID</th>
                    <th>SUBJECT</th>
                    <th>STATUS</th>
                    <th>TREATED ON</th>
                  </tr>
                </thead>
                <tbody>
                  {myReports.map(report => (
                    <tr key={report.reportId}>
                      <td className="monospace critical">{report.reportId}</td>
                      <td>{report.subject}</td>
                      <td>
                        <span className={`status-tag ${report.status}`}>
                          {report.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="date">{new Date(report.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Suspended;
